<template>

  <BasicLayout>

    <div>

      <!-- SEARCH -->
      <Search :searching="searching" @search="search" @closeSearch="searching = false" @clearSearch="clearSearch" />

      <div class="elevation-2">

        <!-- TOOLBAR -->
        <v-toolbar flat="flat">
          <v-toolbar-title>Clips</v-toolbar-title>
          <v-flex class="ml-5" md3="md3">
            <v-text-field v-show="!loading && !searching" v-model="quickSearchID" placeholder="Search by ID"
              :clearable="true"></v-text-field>
          </v-flex>
          <v-spacer></v-spacer>
          <v-icon class="mr-2" v-if="!searching" title="Show the search" @click="searching = true">mdi-magnify</v-icon>
          <v-icon title="Create clip" @click="dialog = true">mdi-plus</v-icon>
        </v-toolbar>

        <!-- TABLE -->
        <Table ref="table" :headers="headers" :items="items" :pagination="pagination"
          :paginationOptions="paginationOptions" :totalItems="pagination.totalItems" :loading="loading"
          @edit="toggleEdit" @del="del" @paginationUpdate="paginationUpdate"></Table>

        <!-- CREATE / EDIT -->
        <Dialog :dialog="dialog" :dialogState="dialogState" :currentEditedClip="editedItem"
          @dialogClosed="close" @createClip="createClip" @editClip="editClip"></Dialog>

      </div>
    </div>

    <!-- LOADER -->
    <LoadingPopup :displayed="loaderDisplayed" :title="loaderTitle" :text="loaderText" />

  </BasicLayout>

</template>

<script>

import BasicLayout from '../layouts/BasicLayout'
import Search from '../components/clips/Search'
import Table from '../components/clips/Table'
import Dialog from '../components/clips/Dialog'
import LoadingPopup from '../components/common/LoadingPopup'

import { Storage } from 'aws-amplify'

export default {
  name: 'ClipsPage',
  components: {
    BasicLayout,
    Search,
    Table,
    Dialog,
    LoadingPopup
  },
  data () {
    return {
      loading: false,

      items: [],

      headers: [
        {
          text: 'ID',
          value: 'id',
          sortable: false,
          width: '1%',
          countries: ['FR', 'US']
        },
        {
          text: 'Sports',
          value: 'sportIDs',
          sortable: false,
          width: '1%',
          countries: ['FR', 'US']
        },
        {
          text: 'Date',
          value: 'publishDate',
          sortable: false,
          width: '1%',
          countries: ['FR', 'US']
        },
        {
          text: 'Title',
          value: 'title',
          sortable: false,
          width: '200px',
          countries: ['FR', 'US']
        },
        {
          text: 'Caption',
          value: 'caption',
          sortable: false,
          width: '200px',
          countries: ['FR', 'US']
        },
        {
          text: 'Clubs',
          value: 'clubs',
          sortable: false,
          width: '1%',
          countries: ['FR', 'US']
        },
        {
          text: 'Teams',
          value: 'teams',
          sortable: false,
          width: '1%',
          countries: ['US']
        },
        {
          text: 'Views',
          value: 'hitCount',
          sortable: false,
          width: '1%',
          countries: ['FR', 'US']
        },
        {
          text: 'Likes',
          value: 'likeCount',
          sortable: false,
          width: '1%',
          countries: ['FR', 'US']
        },
        {
          text: 'Comments',
          value: 'commentCount',
          sortable: false,
          width: '1%',
          countries: ['FR', 'US']
        },
        {
          text: 'Buttons',
          value: null,
          sortable: false,
          width: '90px',
          countries: ['FR', 'US']
        }
      ],

      pagination: {
        page: 1,
        rowsPerPage: 10,
        totalItems: 0
      },
      paginationOptions: [10, 50, 100],

      dialog: null,
      editedItem: null,
      editedIndex: -1,

      quickSearchID: null,

      searching: false,
      searchParams: {},

      loaderDisplayed: false,
      loaderTitle: null,
      loaderText: null,

      globalTopics: {
        FR: {
          prod: 'rematch',
          preprod: 'rematchPreprod',
          dev: 'rematchPreprod'
        },
        US: {
          prod: 'rematchUS',
          preprod: 'rematchPreprodUS',
          dev: 'rematchPreprodUS'
        }
      }
    }
  },
  mounted () {
    this.setHeaders()
    this.loadData()
    this.$store.dispatch('getSports')
  },
  methods: {
    setHeaders () {
      this.headers = this.headers.filter(item => item.countries.includes(this.$country))
    },
    loadData () {
      this.loading = true
      this.searching = false

      let params = {
        page: this.pagination.page - 1,
        pageSize: this.pagination.rowsPerPage,
        types: ['VIDEO'],
        sortBy: 'date:desc',
        withPending: true,
        withTotalItems: true,
        avoidCache: true
      }

      params = {
        ...params,
        ...this.searchParams
      }

      this.$store.dispatch('searchClips', params)
        .then(resp => {
          this.pagination.totalItems = 999999999
          this.items = resp
          this.loading = false
        })
        .catch(() => {
          alert('Error loading clips')
          this.loading = false
        })
    },

    search (params) {
      this.pagination = {
        page: 1,
        rowsPerPage: 10,
        totalItems: 0
      }
      this.searchParams = params
      this.loadData()
    },
    clearSearch () {
      this.searchParams = {}
    },

    close () {
      this.dialog = false
      this.editedItem = null
      this.editedIndex = -1
    },

    async toggleEdit (index) {
      this.editedIndex = index
      this.editedItem = await this.$store.dispatch('getClip', this.items[index].id)
      this.dialog = true
    },

    editClip ({clip, notification}) {
      this.displayLoader({
        title: 'Editing clip',
        text: 'Please wait...'
      })

      let patch = {}
      patch.title = clip.title
      patch.caption = clip.caption
      patch.sportIDs = clip.sportIDs
      patch.videosID = clip.videosID
      patch.primaryClubsID = clip.primaryClubsID
      patch.secondaryClubsID = clip.secondaryClubsID
      patch.publishDate = clip.publishDate

      if (this.$country === 'US') {
        patch.primaryTeamsID = clip.primaryTeamsID
        patch.secondaryTeamsID = clip.secondaryTeamsID
      }

      if (notification) {
        this.$store.dispatch('patchNotification', { id: notification.id, patch: {...notification, notifyAt: clip.publishDate} })
          .then(({id}) => {
            patch.notificationID = id
          })
          .catch(() => {
            alert('Error creating notification')
          })
      }

      this.$store.dispatch('patchClip', {
        id: this.items[this.editedIndex].id,
        patch: patch
      })
        .then(() => {
          this.hideLoader()
          this.updateItem(this.items[this.editedIndex].id)
          this.close()
        })
        .catch(() => {
          this.hideLoader()
          alert('Error editing clip')
        })
    },
    updateItem (id) {
      this.loading = true
      this.$store.dispatch('getClip', id)
        .then(resp => {
          this.items = [resp]
          this.loading = false
        })
        .catch(() => {
          this.loading = false
          alert('Error loading clip')
        })
    },
    labelizedTitle (title, timestamp = Date.now()) {
      const labelizedTitle = title.slice(0, 16).trim()
        .replace(/[^a-zA-Z ]/g, '')
        .replace(/ /g, '-')
      return `${labelizedTitle}-${timestamp}`
    },
    createClip ({ clip, video, notification }) {
      this.displayLoader({
        title: 'Creating clip',
        text: 'Please wait...'
      })

      let timestamp = Date.now()
      let clipParams = {
        ...clip,
        title: encodeURI(clip.title),
        caption: encodeURI(clip.caption),
        isClip: 'true',
        publishDate: clip.publishDate,
        authorID: 'rematch',
        sportIDs: clip.sportIDs.join('&'),
        videosID: clip.videosID.join('&'),
        primaryClubsID: clip.primaryClubsID.join('&'),
        secondaryClubsID: clip.secondaryClubsID.join('&'),
        primaryTeamsID: clip.primaryTeamsID.join('&'),
        secondaryTeamsID: clip.secondaryTeamsID.join('&')
      }

      if (notification) {
        notification.sportIDs = clip.sportIDs
        notification.topics = clip.sportIDs
        notification.data = {clipID: clip.id}
        notification.label = this.labelizedTitle(notification.title.en, timestamp)

        this.displayLoader({
          title: 'Creating notification',
          text: 'Please wait...'
        })

        this.$store.dispatch('createNotification', {...notification})
          .then(({id}) => {
            this.displayLoader({
              title: 'Uploading clip',
              text: 'Please wait...'
            })

            clipParams.notificationID = id

            Storage.put(`clips/${timestamp}.mp4`, this.b64toBlob(video, 'video/mp4', 512), {
              contentType: 'video/mp4',
              metadata: {...clipParams}
            })
              .then(() => {
                this.hideLoader()
                this.close()
              })
              .catch(() => {
                this.hideLoader()
                alert('Error uploading clip')
              })
          })
          .catch(() => {
            this.hideLoader()
            alert('Error creating notification')
          })
      } else {
        this.displayLoader({
          title: 'Uploading clip',
          text: 'Please wait...'
        })
        Storage.put(`clips/${timestamp}.mp4`, this.b64toBlob(video, 'video/mp4', 512), {
          contentType: 'video/mp4',
          metadata: clipParams
        })
          .then(() => {
            this.hideLoader()
            this.close()
          })
          .catch(() => {
            this.hideLoader()
            alert('Error uploading clip')
          })
      }
    },
    b64toBlob (b64Data, contentType, sliceSize) {
      let b64DataContent = b64Data.split(',')[1]
      let byteCharacters = atob(b64DataContent)
      let byteArrays = []

      for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
        let slice = byteCharacters.slice(offset, offset + sliceSize)

        let byteNumbers = new Array(slice.length)
        for (let i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i)
        }

        let byteArray = new Uint8Array(byteNumbers)
        byteArrays.push(byteArray)
      }

      let blob = new Blob(byteArrays, { type: contentType })
      return blob
    },
    del (clip) {
      this.displayLoader({
        title: 'Deleting clip'
      })
      this.$store.dispatch('deleteClip', clip.id)
        .then(() => {
          this.hideLoader()
          this.loadData()
        })
        .catch(() => {
          this.hideLoader()
          alert('Error deleting match')
        })
    },

    displayLoader (content) {
      this.loaderTitle = content.title || null
      this.loaderText = content.text || null
      this.loaderDisplayed = true
    },
    hideLoader () {
      this.loaderDisplayed = false
      this.loaderTitle = null
      this.loaderText = null
    },

    paginationUpdate (v) {
      if (this.pagination.page !== v.page || this.pagination.rowsPerPage !== v.rowsPerPage) {
        this.pagination = {
          page: v.page,
          rowsPerPage: v.rowsPerPage,
          totalItems: this.pagination.totalItems
        }
        this.loadData()
      }
    }

  },
  computed: {
    dialogState () {
      return this.editedIndex === -1 ? 'create' : 'edit'
    }
  },
  watch: {
    'quickSearchID' () {
      if (this.quickSearchID) {
        if (this.quickSearchID.length !== 20) {
          this.quickSearchID = null
          return
        }
        this.loading = true
        this.$store.dispatch('getClip', this.quickSearchID)
          .then(resp => {
            this.items = [resp]
            this.loading = false
          })
          .catch(() => {
            this.loading = false
            alert('Error loading clip')
          })
      } else {
        this.loadData()
      }
    }
  }
}

</script>



// WEBPACK FOOTER //
// src/pages/ClipsPage.vue